import { Modal } from '@round/whitelabel-ui';
import styles from './CreatePurchaseOrder.module.css';
import { FormikProvider, useFormik } from 'formik';
import { FormValues, validationSchema } from '../components/PurchaseOrderForm/helpers';
import { Button } from '@round/whitelabel-ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { creatorbaseQueries } from '@round/api';
import { showNotification } from 'helpers';
import { mapApiErrorsToFormikErrors } from 'utility/utility';
import PurchaseOrderForm from '../components/PurchaseOrderForm/PurchaseOrderForm';
import { useInvalidatePurchaseOrders } from '@round/creatorbase-helpers/api-hooks';

const mutationOptions = creatorbaseQueries.purchaseOrders.options.create;
const mutationUtils = creatorbaseQueries.purchaseOrders.utils.create;

type Props = {
    isOpen: boolean;
    onClose: () => void;
};

const CreatePurchaseOrderModal = ({ isOpen, onClose }: Props) => {
    const queryClient = useQueryClient();
    const { mutate } = useMutation(mutationOptions(queryClient));
    const { invalidatePurchaseOrders } = useInvalidatePurchaseOrders();

    const formik = useFormik<FormValues>({
        initialValues: {
            poNumber: '',
            request_reference: '',
            amount: undefined,
            currency: null,
            description: '',
            billingEntity: null,
            fileUpload: null,
            team: null,
            isInternal: false,
        },
        validationSchema: validationSchema,
        onSubmit: (values, helpers) => {
            helpers.setSubmitting(true);
            mutate(
                {
                    number: values.isInternal ? undefined : values.poNumber,
                    request_reference: values.isInternal ? undefined : values.request_reference,
                    amount: values.amount!,
                    description: values.description,
                    currency_id: values.currency?.value!,
                    billing_entity_id: values.billingEntity?.value!,
                    file_upload: values.fileUpload ?? undefined,
                    team_id: values.team?.value!,
                    is_internal: values.isInternal,
                },
                {
                    onSuccess: () => {
                        showNotification('PO created', 'info');
                        helpers.resetForm();
                        invalidatePurchaseOrders();
                        onClose();
                    },
                    onError: (error) => {
                        if (!mutationUtils.isExpectedErrorResponse(error)) {
                            showNotification('Could not create purchase order', 'error');
                            return;
                        }

                        const response = error.response;
                        if (response.status === 403) {
                            showNotification(response.data.detail, 'error');
                            return;
                        }

                        helpers.setErrors(
                            mapApiErrorsToFormikErrors({
                                fileUpload: response.data.file_upload,
                                billingEntity: response.data.billing_entity_id,
                                currency: response.data.currency_id,
                                description: response.data.description,
                                request_reference: response.data.request_reference,
                                amount: response.data.amount,
                                poNumber: response.data.number,
                                team: response.data.team_id,
                            })
                        );
                    },
                    onSettled: () => {
                        helpers.setSubmitting(false);
                    },
                }
            );
        },
    });

    const handleClose = () => {
        onClose();
        formik.resetForm();
    };

    return (
        <Modal className={styles.modal} closeOnEscape closeOnOverlayClick isOpen={isOpen} onClose={handleClose}>
            <Modal.Header>
                <Modal.Title>Create Purchase Order</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <FormikProvider value={formik}>
                    <PurchaseOrderForm />
                </FormikProvider>
            </Modal.Body>

            <Modal.Actions>
                <Button
                    appearance="primary"
                    type="button"
                    onClick={formik.submitForm}
                    isLoading={formik.isSubmitting}
                    disabled={formik.isSubmitting || !formik.dirty}
                >
                    Create PO
                </Button>
                <Button appearance="ghost" type="button" onClick={handleClose}>
                    Cancel
                </Button>
            </Modal.Actions>
        </Modal>
    );
};

export default CreatePurchaseOrderModal;
